// src/hooks/useAuthenticationAttribution.ts
import { useState, useEffect } from "react";

interface AttributionData {
  utmSource: string | undefined;
  utmMedium: string | undefined;
  utmCampaignId: string | undefined;
  utmCampaignName: string | undefined;
  utmTerm: string | undefined;
  utmContent: string | undefined;
  gclid: string | undefined;
  msclkid: string | undefined;
  oppref: string | undefined;
  visitorId: string | undefined;
  referrer: string | undefined;
  landingPage: string | undefined;
  firstTouchReferrer: string | undefined;
  firstTouchLandingPage: string | undefined;
  attributionSource: "cookie" | "localStorage" | "legacy" | "none";
}

export function useAuthenticationAttribution(): AttributionData {
  const [attribution, setAttribution] = useState<AttributionData>({
    utmSource: undefined,
    utmMedium: undefined,
    utmCampaignId: undefined,
    utmCampaignName: undefined,
    utmTerm: undefined,
    utmContent: undefined,
    gclid: undefined,
    msclkid: undefined,
    oppref: undefined,
    visitorId: undefined,
    referrer: undefined,
    landingPage: undefined,
    firstTouchReferrer: undefined,
    firstTouchLandingPage: undefined,
    attributionSource: "none",
  });

  useEffect(() => {
    try {
      // First, try to get attribution from new cookies (primary source)
      const cookieSource = getCookieSource();
      if (cookieSource.attributionSource !== "none") {
        setAttribution(cookieSource);
        return;
      }

      // If cookies don't have attribution, try localStorage
      const localStorageSource = getLocalStorageSource();
      if (localStorageSource.attributionSource !== "none") {
        setAttribution(localStorageSource);
        return;
      }

      // Finally, try legacy cookies
      const legacySource = getLegacySource();
      if (legacySource.attributionSource !== "none") {
        setAttribution(legacySource);
        return;
      }

      // If we got here, we didn't find any attribution data
      // No need to update state since it's already initialized with 'none'
    } catch (e) {
      console.warn("Error retrieving attribution data", e);
    }
  }, []);

  function getFirstTouchFromCookies() {
    return {
      firstTouchReferrer: getCookie("ft_ref"),
      firstTouchLandingPage: getCookie("ft_lp"),
    };
  }

  // Get attribution from new format cookies
  function getCookieSource(): AttributionData {
    const utmSource = getCookie("fs_src");
    const utmMedium = getCookie("fs_med");
    const utmCampaignId = getCookie("fs_cmp");
    const utmCampaignName = getCookie("fs_cmp_name");
    const utmTerm = getCookie("fs_trm");
    const utmContent = getCookie("fs_cnt");
    const gclid = getCookie("fs_gid");
    const msclkid = getCookie("fs_mid");
    const oppref = getCookie("fs_opr");
    const visitorId = getCookie("fs_vid");
    const referrer = getCookie("fs_ref");
    const landingPage = getCookie("fs_lp");
    const firstTouch = getFirstTouchFromCookies();

    // Check if we have any attribution data
    const hasData =
      utmSource ||
      utmMedium ||
      utmCampaignId ||
      gclid ||
      msclkid ||
      oppref ||
      referrer ||
      landingPage ||
      firstTouch.firstTouchReferrer ||
      firstTouch.firstTouchLandingPage;

    if (hasData) {
      return {
        utmSource,
        utmMedium,
        utmCampaignId,
        utmCampaignName,
        utmTerm,
        utmContent,
        gclid,
        msclkid,
        oppref,
        visitorId,
        referrer,
        landingPage,
        ...firstTouch,
        attributionSource: "cookie",
      };
    }

    return {
      utmSource: undefined,
      utmMedium: undefined,
      utmCampaignId: undefined,
      utmCampaignName: undefined,
      utmTerm: undefined,
      utmContent: undefined,
      gclid: undefined,
      msclkid: undefined,
      oppref: undefined,
      visitorId: undefined,
      referrer: undefined,
      landingPage: undefined,
      firstTouchReferrer: undefined,
      firstTouchLandingPage: undefined,
      attributionSource: "none",
    };
  }

  // Get attribution from localStorage
  // oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
  function getLocalStorageSource(): AttributionData {
    try {
      const storedData = localStorage.getItem("fs_data");
      if (storedData) {
        const data = JSON.parse(storedData);
        const referrer = data.ref || undefined;
        const landingPage = data.lp || undefined;

        // Try first-touch from localStorage, fall back to cookies
        let firstTouchReferrer: string | undefined;
        let firstTouchLandingPage: string | undefined;
        try {
          const ftData = localStorage.getItem("ft_data");
          if (ftData) {
            const ft = JSON.parse(ftData);
            firstTouchReferrer = ft.ref || undefined;
            firstTouchLandingPage = ft.lp || undefined;
          }
        } catch {
          // ignore
        }
        if (!firstTouchReferrer && !firstTouchLandingPage) {
          const ftCookies = getFirstTouchFromCookies();
          firstTouchReferrer = ftCookies.firstTouchReferrer;
          firstTouchLandingPage = ftCookies.firstTouchLandingPage;
        }

        // Check if we have any attribution data
        const hasData =
          data.src ||
          data.med ||
          data.cmp ||
          data.gid ||
          data.mid ||
          data.opr ||
          referrer ||
          landingPage ||
          firstTouchReferrer ||
          firstTouchLandingPage;

        if (hasData) {
          return {
            utmSource: data.src || undefined,
            utmMedium: data.med || undefined,
            utmCampaignId: data.cmp || undefined,
            utmCampaignName: data.cmp_name || undefined,
            utmTerm: data.trm || undefined,
            utmContent: data.cnt || undefined,
            gclid: data.gid || undefined,
            msclkid: data.mid || undefined,
            oppref: data.opr || undefined,
            visitorId: data.vid || undefined,
            referrer,
            landingPage,
            firstTouchReferrer,
            firstTouchLandingPage,
            attributionSource: "localStorage",
          };
        }
      }
    } catch (e) {
      console.warn("Error reading attribution from localStorage", e);
    }

    return {
      utmSource: undefined,
      utmMedium: undefined,
      utmCampaignId: undefined,
      utmCampaignName: undefined,
      utmTerm: undefined,
      utmContent: undefined,
      gclid: undefined,
      msclkid: undefined,
      oppref: undefined,
      visitorId: undefined,
      referrer: undefined,
      landingPage: undefined,
      firstTouchReferrer: undefined,
      firstTouchLandingPage: undefined,
      attributionSource: "none",
    };
  }

  // Get attribution from legacy cookies
  function getLegacySource(): AttributionData {
    const utmSource = getCookie("utmSource");
    const utmMedium = getCookie("utmMedium");
    const utmCampaignId = getCookie("utmCampaign");
    const utmTerm = getCookie("utmTerm");
    const gclid = getCookie("gclid");
    const msclkid = getCookie("msclkid");
    const firstTouch = getFirstTouchFromCookies();

    // Check if we have any attribution data
    const hasData = utmSource || utmMedium || utmCampaignId || gclid || msclkid;

    if (hasData) {
      return {
        utmSource,
        utmMedium,
        utmCampaignId,
        utmCampaignName: undefined, // Not available in legacy format
        utmTerm,
        utmContent: undefined, // Not available in legacy format
        gclid,
        msclkid,
        oppref: undefined, // Not available in legacy format
        visitorId: undefined, // Not available in legacy format
        referrer: undefined,
        landingPage: undefined,
        ...firstTouch,
        attributionSource: "legacy",
      };
    }

    return {
      utmSource: undefined,
      utmMedium: undefined,
      utmCampaignId: undefined,
      utmCampaignName: undefined,
      utmTerm: undefined,
      utmContent: undefined,
      gclid: undefined,
      msclkid: undefined,
      oppref: undefined,
      visitorId: undefined,
      referrer: undefined,
      landingPage: undefined,
      firstTouchReferrer: undefined,
      firstTouchLandingPage: undefined,
      attributionSource: "none",
    };
  }

  // Helper function to get a cookie value
  function getCookie(name: string): string | undefined {
    const match = document.cookie.match(
      new RegExp("(^| )" + name + "=([^;]+)"),
    );
    const value = match?.[2];
    if (value !== undefined) return decodeURIComponent(value);
    return undefined;
  }

  return attribution;
}
